import {isFeatureEnabled} from 'feature-flags';

import {head, isEmpty, isNotNil} from 'ramda';

import {featureFlags} from '@dms/feature-flags';

import {useSourcingTable} from '../../../components/SourcingTable/hooks/useSourcingTable';
import {REAL_TIME_POLLING_INTERVAL} from '../../../constants/REAL_TIME_POLLING_INTERVAL';
import {useAuctionEnumLookup} from '../../../hooks/useAuctionEnumLookup';
import {useSourcingListAvailability} from '../../../hooks/useSourcingListAvailability';
import {SourcingType} from '../../../hooks/useSourcingType';
import {SourcingTableQueryResult} from '../../../types/SourcingTableQueryResult';
import {adaptAuctionVehicleToSourcingItem} from '../../../utils/adaptAuctionVehicleToSourcingItem';
import {FiltersType} from '../types/FiltersType';
import {getApplicableFilters} from '../utils/getApplicableFilters';
import {hasSettledAllOffersListResponse} from '../utils/hasSettledAllOffersListResponse';
import {hasSomeFilters} from '../utils/hasSomeFilters';
import {isSuspiciousReasonFilterEnabled} from '../utils/isSuspiciousReasonFilterEnabled';
import {useAuctionsVehicleListQueries} from './useAuctionsVehicleListQueries';
import {useClassifiedsVehicleListQueries} from './useClassifiedsVehicleListQueries';
import {useFilterEditingSession} from './useFilterEditingSession';
import {SourcingFilterPresetsControl} from './useSourcingFilterPresets';

type ListingOptions = Pick<
  Parameters<typeof useClassifiedsVehicleListQueries>[0],
  'currency' | 'pricingStrategy'
> & {
  orderBy: Parameters<typeof useAuctionsVehicleListQueries>[0]['orderBy'];
  listingBodies: Pick<
    NonNullable<ReturnType<typeof useFilterEditingSession>['listingSnapshot']>,
    'classifieds' | 'auctions'
  > | null;
  sourcingType: SourcingType;
  isEditing: boolean;
  isMapView: boolean;
  isLivePollingEnabled: boolean;
  state: ReturnType<typeof useSourcingTable>[0];
  country: Parameters<typeof useAuctionsVehicleListQueries>[0]['buyerCountry'];
  enumLookup: ReturnType<typeof useAuctionEnumLookup>;
  filterPresetsControl: SourcingFilterPresetsControl;
  appliedFilters: FiltersType;
  count: number | undefined;
  listingSnapshot: ReturnType<typeof useFilterEditingSession>['listingSnapshot'];
  selectedAdId: string | undefined;
};

export function useAllOffersListing(options: ListingOptions) {
  const {
    listingBodies,
    sourcingType,
    isEditing,
    isMapView,
    isLivePollingEnabled,
    state,
    country,
    currency,
    pricingStrategy,
    orderBy,
    enumLookup,
    filterPresetsControl,
    appliedFilters,
    count,
    listingSnapshot,
    selectedAdId,
  } = options;
  const isVanFiltersEnabled = isFeatureEnabled(featureFlags.SOURCING_VAN_FILTERS);
  const isOriginCountryFilterEnabled = isFeatureEnabled(
    featureFlags.SOURCING_ORIGIN_COUNTRY_FILTER
  );
  const classifiedsQueryResult = useClassifiedsVehicleListQueries({
    filterBody: listingBodies?.classifieds ?? null,
    orderBy,
    currency,
    pricingStrategy,
    page: state.page,
    isFiltersOpen: isEditing,
    isRealTime: isLivePollingEnabled,
    pollingInterval: REAL_TIME_POLLING_INTERVAL,
    skip: sourcingType === SourcingType.AUCTIONS || isMapView,
  });
  const auctionsQueryResult = useAuctionsVehicleListQueries({
    filterBody: listingBodies?.auctions ?? null,
    orderBy,
    currency,
    buyerCountry: country,
    pricingStrategy,
    isFiltersOpen: isEditing,
    isRealTime: isLivePollingEnabled,
    page: state.page,
    pollingInterval: REAL_TIME_POLLING_INTERVAL,
    skip: sourcingType === SourcingType.CLASSIFIEDS,
  });

  const listQueryResult =
    sourcingType === SourcingType.CLASSIFIEDS ? classifiedsQueryResult : auctionsQueryResult;
  const data =
    sourcingType === SourcingType.CLASSIFIEDS
      ? (isEditing ? classifiedsQueryResult.data : classifiedsQueryResult.currentData)?.data
      : (isEditing ? auctionsQueryResult.data : auctionsQueryResult.currentData)?.data.map((item) =>
          adaptAuctionVehicleToSourcingItem(item, enumLookup)
        );

  const rows = data ?? [];
  const activeFilterBody =
    sourcingType === SourcingType.CLASSIFIEDS
      ? listingBodies?.classifieds
      : listingBodies?.auctions;
  const isListQueryEnabled = isNotNil(activeFilterBody) && !isMapView;
  const hasSettledListResponse = hasSettledAllOffersListResponse({
    data,
    isEndReached: listQueryResult.isEndReached,
    isLoading: listQueryResult.isLoading,
    isFetching: listQueryResult.isFetching,
    isListQueryEnabled,
  });
  const isFetchingFreshData =
    !isEditing &&
    (filterPresetsControl.isLoading || (isListQueryEnabled && !hasSettledListResponse));
  // Filters that do not apply here have no chip, so they must not highlight the filter button.
  const hasFilters = hasSomeFilters(
    getApplicableFilters(appliedFilters, sourcingType, {
      isVanFiltersEnabled,
      isOriginCountryFilterEnabled,
      isSuspiciousVehiclesEnabled: isFeatureEnabled(featureFlags.SOURCING_SUSPICIOUS_VEHICLES),
      isSuspiciousReasonFilterEnabled: isSuspiciousReasonFilterEnabled(),
    })
  );
  const tableQueryResult: SourcingTableQueryResult = {
    data: rows,
    count: isEditing ? listingSnapshot?.count : count,
    isLoading: isFetchingFreshData,
    isFetching: listQueryResult.isFetching,
    error: listQueryResult.error,
    onRetry: listQueryResult.reset,
    lastUpdateTime: listQueryResult.fulfilledTimeStamp,
    isPolling: listQueryResult.isPolling,
    scrollAttemptedOffset: listQueryResult.scrollAttemptedOffset,
    isEndReached: listQueryResult.isEndReached,
  };
  const hasCompletedWithNoVisibleResults =
    !isMapView &&
    !filterPresetsControl.isLoading &&
    !listQueryResult.isError &&
    isEmpty(rows) &&
    hasSettledListResponse &&
    (isEditing ? listingSnapshot?.count : count) === 0;

  // Look up the selected row by ID because the saved row can have stale pricing after a currency switch.
  const preview = rows.find((row) => row.sourcingVehicle.adId === selectedAdId) ?? head(rows);

  const listQueryKey = JSON.stringify({
    filterBody: activeFilterBody,
    orderBy: state.orderBy,
    currency,
    country,
    pricingStrategy,
  });

  const classifiedsAdIds =
    sourcingType === SourcingType.CLASSIFIEDS ? rows.map((row) => row.sourcingVehicle.adId) : [];
  const availabilityUserLoadKey = `${listQueryKey}|${listQueryResult.scrollAttemptedOffset ?? 0}`;
  useSourcingListAvailability({
    adIds: classifiedsAdIds,
    enabled:
      isFeatureEnabled(featureFlags.SOURCING_AVAILABILITY_CHECK) &&
      sourcingType === SourcingType.CLASSIFIEDS &&
      !isEditing,
    userLoadKey: availabilityUserLoadKey,
  });

  return {
    tableQueryResult,
    hasCompletedWithNoVisibleResults,
    preview,
    listQueryKey,
    isFetchingFreshData,
    hasFilters,
  };
}
